import { z } from 'zod';
import { optionalFinanceCurrencySchema } from './finance-currency.schema.js';

export const ActionTypeSchema = z.enum(['risk_treatment', 'corrective', 'preventive', 'improvement']);

export const PdcaStatusSchema = z.enum(['plan', 'do', 'check', 'act', 'closed']);

export const ActionSourceTypeSchema = z.enum([
    'management-review',
    'audit',
    'risk',
    'incident',
    'vendor',
    'crisis-event',
    'exercise',
    'other',
]);

export const RiskTreatmentSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    _id: z.string().optional(),
    tenantId: z.string().optional(),
    actionType: ActionTypeSchema.optional(),
    title: z.string().min(1, "form.validation.titleRequired"),
    code: z.string().trim().max(80).optional().or(z.literal('')),
    description: z.string().optional(),
    strategy: z.string().default('Mitigation'),
    plan: z.string().optional(),
    rootCause: z.string().optional(),
    rootCauseReasons: z.array(z.string()).optional(),
    preventiveRationale: z.string().optional(),
    improvementObjective: z.string().optional(),
    owner: z.string().optional(),
    customFields: z.record(z.unknown()).optional(),
    unit: z.string().optional(),
    startDate: z.union([z.string(), z.date()]).optional(),
    deadline: z.union([z.string(), z.date()]).optional(),
    progress: z.number().min(0).max(100).default(0),
    budget: z.number().min(0).optional().default(0),
    currency: optionalFinanceCurrencySchema,
    budgetStatus: z.string().min(1, "form.validation.budgetStatusRequired").default('not budgeted for'),
    status: z.string().optional(),
    sourceType: z.string().optional(),
    sourceId: z.string().optional(),
    sourceTitle: z.string().optional(),
    rootCauseAnalysis: z.string().optional(),
    rootCauseMethod: z.string().optional(),
    effectivenessVerification: z.string().optional(),
    verifiedBy: z.string().optional(),
    verifiedDate: z.union([z.string(), z.date()]).optional(),
    pdcaStatus: z.string().optional(),
    createdAt: z.union([z.date(), z.string()]).optional(),
    updatedAt: z.union([z.date(), z.string()]).optional(),
});

export const CreateRiskTreatmentSchema = RiskTreatmentSchema.omit({ id: true, _id: true, createdAt: true, updatedAt: true, status: true }).extend({
    actionType: ActionTypeSchema.default('risk_treatment')
});
export const UpdateRiskTreatmentSchema = RiskTreatmentSchema.partial();

/** Unified action (same as risk treatment payload + actionType) */
export const CreateActionSchema = CreateRiskTreatmentSchema;
export const UpdateActionSchema = UpdateRiskTreatmentSchema;

// Frontend Form Schema
export const RiskTreatmentFormSchema = z.object({
    title: z.string().min(1, "form.validation.titleRequired"),
    code: z.string().trim().max(80).optional().or(z.literal('')),
    description: z.string().optional(),
    actionType: ActionTypeSchema.optional(),
    strategy: z.string().optional(),
    plan: z.string().optional(),
    startDate: z.union([z.string(), z.date()]).optional(),
    deadline: z.union([z.string(), z.date()]).optional(),
    owner: z.string().optional(),
    customFields: z.record(z.unknown()).optional(),
    unit: z.string().optional(),
    progress: z.number().min(0).max(100).default(0),
    budget: z.number().min(0).optional().default(0),
    currency: optionalFinanceCurrencySchema,
    budgetStatus: z.string().min(1, "form.validation.budgetStatusRequired"),
    rootCause: z.string().optional(),
    rootCauseReasons: z.array(z.string()).optional().default([]),
    preventiveRationale: z.string().optional(),
    improvementObjective: z.string().optional(),
    sourceType: z.string().optional().or(z.literal('')),
    sourceId: z.string().optional().or(z.literal('')),
    sourceTitle: z.string().optional().or(z.literal('')),
    rootCauseAnalysis: z.string().optional().or(z.literal('')),
    rootCauseMethod: z.string().optional().or(z.literal('')),
    effectivenessVerification: z.string().optional().or(z.literal('')),
    verifiedBy: z.string().optional().or(z.literal('')),
    verifiedDate: z.union([z.string(), z.date()]).optional(),
    pdcaStatus: PdcaStatusSchema.optional(),
});

/** Actions page: same fields + required action type; type-specific fields validated in superRefine */
export const ActionFormSchema = RiskTreatmentFormSchema.omit({ title: true }).extend({
    actionType: ActionTypeSchema,
    title: z.string().min(1, 'actionsForm.validation.titleRequired'),
}).superRefine((data, ctx) => {
    const at = data.actionType;
    const reasonIds = Array.isArray(data.rootCauseReasons) ? data.rootCauseReasons.filter(Boolean) : [];
    if (at === 'corrective' && reasonIds.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'actionsForm.validation.rootCauseReasonsRequired',
            path: ['rootCauseReasons'],
        });
    }
    if (at === 'preventive' && !String(data.preventiveRationale ?? '').trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'actionsForm.validation.preventiveRationaleRequired',
            path: ['preventiveRationale'],
        });
    }
    if (at === 'improvement' && !String(data.improvementObjective ?? '').trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'actionsForm.validation.improvementObjectiveRequired',
            path: ['improvementObjective'],
        });
    }
});

// Frontend Form Schema for Risk Treatment Activities
export const RiskTreatmentActivitiesFormSchema = z.object({
    title: z.string().min(1, "form.validation.titleRequired"),
    description: z.string().optional(),
    riskId: z.union([z.number(), z.string()]).optional(),
    actionType: ActionTypeSchema.optional(),
    strategy: z.string().optional(),
    plan: z.string().optional(),
    startDate: z.union([z.string(), z.date()]).optional(),
    deadline: z.union([z.string(), z.date()]).optional(),
    owner: z.string().optional(),
    customFields: z.record(z.unknown()).optional(),
    unit: z.string().optional(),
    progress: z.number().min(0).max(100).default(0),
    budget: z.number().min(0).optional().default(0),
    currency: optionalFinanceCurrencySchema,
    budgetStatus: z.string().min(1, "form.validation.budgetStatusRequired"),
    status: z.string().optional(),
});
